import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Sun, Egg, Fish, Moon, Dumbbell, Droplets, UtensilsCrossed, Pill, Ban,
  ShieldAlert, Clock, Salad, AlertTriangle, ArrowUpRight,
} from "lucide-react";

const LANDSCAPE = [
  { cat: "Vitamin D", icon: Sun, skip: "Your routine did not flag a sun or dietary gap — nothing to fix." },
  { cat: "Vitamin B12", icon: Egg, skip: "Your eating pattern covers B12 sources well enough." },
  { cat: "Omega-3 (EPA+DHA)", icon: Fish, skip: "Your answers suggest your fish habit covers EPA & DHA." },
  { cat: "Magnesium", icon: Moon, skip: "Your whole grains and pulses already cover the common gap." },
  { cat: "Creatine monohydrate", icon: Dumbbell, skip: "It only earns a place with regular resistance training — not your current pattern." },
  { cat: "Protein powder", icon: UtensilsCrossed, skip: "Food anchors cover your protein — no convenience gap was flagged." },
  { cat: "Electrolytes", icon: Droplets, skip: "No heavy heat or sweat context in your answers — water is enough." },
  { cat: "Multivitamin (basic)", icon: Pill, skip: "Your pattern is varied enough — the insurance policy is not needed." },
  { cat: "Fat burners & detox", icon: Ban, suppressed: true, skip: "Suppressed everywhere on this platform: no evidence, real risks. Never in any plan." },
];

const STATUS_STYLE = {
  verify: { ring: "bg-clay text-bone border-clay", chip: "bg-clay/10 text-clay border-clay/30", label: "VERIFY — in your plan" },
  consider: { ring: "bg-sage text-bone border-sage", chip: "bg-sage/10 text-sage border-sage/40", label: "CONSIDER — in your plan" },
  skip: { ring: "bg-sand text-slatem border-ink/15", chip: "bg-ink/5 text-slatem border-ink/10", label: "not in your plan" },
  suppressed: { ring: "bg-bone text-clay border-clay/40", chip: "bg-clay/10 text-clay border-clay/30", label: "SUPPRESSED" },
  gated: { ring: "bg-sand text-slatem/60 border-ink/10", chip: "bg-ink/5 text-slatem/70 border-ink/10", label: "paused by gate" },
};

export default function SupplementGuide({ supplements = [], safetyState = "standard" }) {
  const gated = safetyState !== "standard";

  const stations = useMemo(
    () =>
      LANDSCAPE.map((g) => {
        const mine = supplements.find((s) => s.category === g.cat);
        const status = g.suppressed ? "suppressed" : gated ? "gated" : mine ? mine.state : "skip";
        return { ...g, mine, status };
      }),
    [supplements, gated]
  );

  const firstInPlan = stations.find((s) => s.mine && !gated);
  const [selected, setSelected] = useState(firstInPlan ? firstInPlan.cat : stations[0].cat);
  const active = stations.find((s) => s.cat === selected);
  const earned = supplements.length;

  return (
    <section className="my-14" data-testid="supplement-guide">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-clay mb-3">the supplement landscape</p>
        <h2 className="font-serif text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95]" data-testid="supplement-guide-title">
          {gated ? "The safety gate paused every supplement." : earned > 0 ? `${earned} earned a place. ${LANDSCAPE.length - earned - 1} did not.` : "None earned a place — and that is the point."}
        </h2>
        <p className="mt-3 text-sm sm:text-base text-slatem max-w-xl leading-relaxed">
          {gated
            ? "The map below shows what was evaluated and why nothing appears. Bring it to your doctor or pharmacist — that conversation is the supplement plan in this mode."
            : "Every common category was run against your answers, the evidence grades and the safety gate. Tap any station to see the verdict."}
        </p>
      </motion.div>

      {gated && (
        <div className="mt-6 flex items-start gap-3 rounded-2xl border border-clay/40 bg-clay/5 p-4 sm:p-5" data-testid="supplement-guide-gate-banner">
          <ShieldAlert className="h-5 w-5 text-clay shrink-0 mt-0.5" />
          <p className="text-sm leading-relaxed text-ink/80">
            Clinician-safe mode: no product, dose or brand appears here. Questions worth asking your clinician:
            “Do any of my medicines interact with common supplements?” and “Should I test anything before supplementing?”
          </p>
        </div>
      )}

      {/* interactive path */}
      <div className="relative mt-10">
        <div className="absolute top-6 left-6 right-6 h-px bg-ink/15 hidden md:block" aria-hidden />
        <div className="grid grid-cols-3 md:grid-cols-9 gap-y-8 gap-x-2 relative" data-testid="supplement-guide-path">
          {stations.map((s, i) => {
            const st = STATUS_STYLE[s.status];
            const Icon = s.icon;
            const isActive = selected === s.cat;
            return (
              <motion.button
                key={s.cat}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                onClick={() => setSelected(s.cat)}
                data-testid={`supplement-station-${i}`}
                aria-pressed={isActive}
                className="group flex flex-col items-center gap-2.5 min-h-[44px]"
              >
                <span className={`relative flex h-12 w-12 items-center justify-center rounded-full border-2 transition-all duration-200 ${st.ring} ${isActive ? "scale-110 shadow-lg" : "group-hover:scale-105"}`}>
                  <Icon className="h-5 w-5" />
                  {s.mine && !gated && (
                    <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-clay border-2 border-bone zb-vitality-dot" />
                  )}
                </span>
                <span className={`text-[11px] sm:text-xs font-semibold text-center leading-tight ${s.status === "suppressed" ? "line-through text-clay/80" : "text-ink/80"}`}>
                  {s.cat}
                </span>
                <span className={`rounded-full border px-2 py-0.5 font-mono text-[8px] sm:text-[9px] uppercase tracking-[0.12em] ${st.chip}`}>
                  {st.label}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* detail panel */}
      <AnimatePresence mode="wait">
        {active && (
          <motion.div
            key={active.cat}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className={`mt-8 rounded-3xl border p-6 sm:p-8 ${active.status === "suppressed" ? "border-clay/40 bg-clay/5" : active.mine && !gated ? "border-sage/50 bg-sage/5" : "border-ink/12 bg-sand/60"}`}
            data-testid="supplement-detail"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <h3 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight" data-testid="supplement-detail-title">{active.cat}</h3>
              <div className="flex items-center gap-2">
                {active.mine?.grade && (
                  <span className="rounded-full bg-ink text-bone px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em]" data-testid="supplement-detail-grade">
                    Grade {active.mine.grade}
                  </span>
                )}
                <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLE[active.status].chip}`} data-testid="supplement-detail-status">
                  {STATUS_STYLE[active.status].label}
                </span>
              </div>
            </div>

            {gated ? (
              <p className="text-sm sm:text-base text-ink/80 leading-relaxed">
                {active.suppressed
                  ? active.skip
                  : `${active.cat} stays paused in clinician-safe mode. If your doctor clears supplements generally, retake the test with an updated safety answer and this category will be re-evaluated against your routine.`}
              </p>
            ) : active.mine ? (
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slatem mb-1.5">Why it earned a place</p>
                  <p className="text-sm sm:text-base text-ink/85 leading-relaxed">{active.mine.why}</p>
                  <p className="mt-2 text-sm text-slatem leading-relaxed">{active.mine.note}</p>
                </div>
                <div className="space-y-4">
                  {active.mine.timing && (
                    <div className="flex items-start gap-2.5">
                      <Clock className="h-4 w-4 text-clay shrink-0 mt-0.5" />
                      <p className="text-sm text-ink/85"><span className="font-semibold">Timing: </span>{active.mine.timing}</p>
                    </div>
                  )}
                  {active.mine.food_sources?.length > 0 && (
                    <div className="flex items-start gap-2.5">
                      <Salad className="h-4 w-4 text-sage shrink-0 mt-1" />
                      <div>
                        <p className="text-sm font-semibold mb-1.5">Food first</p>
                        <div className="flex flex-wrap gap-1.5">
                          {active.mine.food_sources.map((f) => (
                            <span key={f} className="rounded-full bg-bone border border-ink/15 px-2.5 py-1 text-[11px] text-ink/75">{f}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {active.mine.safety && (
                    <div className="flex items-start gap-2.5">
                      <AlertTriangle className="h-4 w-4 text-clay shrink-0 mt-0.5" />
                      <p className="text-xs text-clay/90 leading-relaxed">{active.mine.safety}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm sm:text-base text-ink/80 leading-relaxed">{active.skip}</p>
            )}

            {active.mine?.evidence_id && !gated && (
              <Link to="/evidence" data-testid="supplement-detail-evidence-link"
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-ink/70 hover:text-clay transition-colors">
                See the graded evidence <ArrowUpRight className="h-4 w-4" />
              </Link>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-slatem" data-testid="supplement-guide-note">
        max two categories per plan · food before pills · nothing here is for sale
      </p>
    </section>
  );
}
